import { useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, Download, ExternalLink, Shield, Globe, 
  FileCode2, AlertTriangle, CheckCircle, Clock, Hash,
  Lock, Cookie, Database, Key, Activity, XCircle
} from "lucide-react";
import { Link } from "wouter";
import { formatDistanceToNow } from "date-fns";

interface ScanReport {
  id: number;
  url: string;
  type: string;
  status: string;
  results: string;
  createdAt: string;
  updatedAt: string;
}

export default function ScanReport() {
  const params = useParams();
  const scanId = params.id;

  const { data: scan, isLoading } = useQuery<ScanReport>({
    queryKey: ["/api/scans", scanId],
    enabled: !!scanId,
  });

  const { data: scriptData } = useQuery({
    queryKey: ["/api/scans", scanId, "scripts"],
    enabled: !!scanId,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="h-8 w-8 bg-slate-300 rounded animate-pulse"></div>
          <div className="h-6 w-48 bg-slate-300 rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-4 w-24 bg-slate-300 rounded animate-pulse"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-slate-300 rounded animate-pulse"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium mb-2">Scan not found</h3>
        <p className="text-slate-600 dark:text-slate-400 mb-4">
          The requested scan report could not be found.
        </p>
        <Link href="/">
          <Button>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>
      </div>
    );
  }

  const results = scan.results ? JSON.parse(scan.results) : null;
  const thirdPartyDomains = results?.thirdPartyDomains || [];
  const scripts = results?.scripts || [];
  const highRiskScripts = scripts.filter((script: any) => 
    script.containsEval || script.containsFunction || script.entropy > 6
  );
  
  // Calculate improved security score using weighted average of components
  // This matches the improved backend calculation for consistency
  
  // Normalize cookies to always be an array (for security score calculation)
  let cookies: any[] = [];
  if (results?.cookies) {
    if (Array.isArray(results.cookies)) {
      cookies = results.cookies;
    } else if (typeof results.cookies === 'object') {
      // If cookies is an object, convert to array
      cookies = Object.values(results.cookies);
    } else {
      // Single cookie value
      cookies = [results.cookies];
    }
  }
  
  const vulnerabilities = results?.vulnerabilities || [];
  const securityHeaders = results?.securityHeaders || {};
  const sslCertificate = results?.sslCertificate;
  const dangerousScripts = scripts.filter((s: any) => s.containsEval || s.containsFunction);
  const totalScripts = scripts.length || 1;
  
  // Component 1: CSP Headers Score (25% weight)
  // Simplified CSP score calculation for frontend
  const hasCSP = securityHeaders['content-security-policy'] && 
                 securityHeaders['content-security-policy'] !== 'Missing' &&
                 securityHeaders['content-security-policy'].trim() !== '';
  let cspScore = hasCSP ? 60 : 20; // Base score: 60 if CSP exists, 20 if not
  if (hasCSP) {
    const cspHeader = securityHeaders['content-security-policy'];
    // Check for unsafe directives
    if (cspHeader.includes("'unsafe-inline'")) cspScore -= 15;
    if (cspHeader.includes("'unsafe-eval'")) cspScore -= 15;
    // Bonus for good directives
    if (cspHeader.includes("'self'")) cspScore += 10;
    if (cspHeader.includes("object-src 'none'")) cspScore += 5;
  }
  cspScore = Math.max(20, Math.min(100, cspScore));
  
  // Component 2: Script Security & Integrity (20% weight)
  let scriptSecurityScore = 60; // Base score
  if (totalScripts > 0) {
    const highEntropyScripts = scripts.filter((s: any) => s.entropy > 6).length;
    const inlineScripts = scripts.filter((s: any) => s.src === 'inline' || !s.src).length;
    const safeScripts = totalScripts - dangerousScripts.length - highEntropyScripts - inlineScripts;
    const safeRatio = safeScripts / totalScripts;
    
    scriptSecurityScore += Math.round(safeRatio * 40); // Up to +40 for safe scripts
    scriptSecurityScore -= Math.round((dangerousScripts.length / totalScripts) * 25); // Reduced penalty
    scriptSecurityScore -= Math.round((highEntropyScripts / totalScripts) * 15); // Reduced penalty
    scriptSecurityScore -= Math.round((inlineScripts / totalScripts) * 10); // Reduced penalty
    
    // Bonus for SRI
    const externalScripts = scripts.filter((s: any) => s.src && s.src !== 'inline' && !s.src.startsWith('data:'));
    const scriptsWithSRI = scripts.filter((s: any) => s.hasIntegrity || s.integrity).length;
    if (externalScripts.length > 0) {
      const sriRatio = scriptsWithSRI / externalScripts.length;
      scriptSecurityScore += Math.round(sriRatio * 15);
    }
  }
  scriptSecurityScore = Math.max(40, Math.min(100, scriptSecurityScore));
  
  // Component 3: Security Headers (15% weight)
  let headersScore = 30; // Base score
  const headerPoints = {
    'content-security-policy': 20,
    'strict-transport-security': 15,
    'x-frame-options': 12,
    'x-content-type-options': 12,
    'x-xss-protection': 8,
    'referrer-policy': 8,
    'permissions-policy': 5
  };
  
  Object.entries(headerPoints).forEach(([header, points]) => {
    const headerValue = securityHeaders[header] || securityHeaders[header.replace(/-/g, '')];
    if (headerValue && headerValue !== 'Missing' && headerValue.trim() !== '') {
      headersScore += points;
    }
  });
  headersScore = Math.max(30, Math.min(100, headersScore));
  
  // Component 4: Cookie Security (10% weight)
  let cookieScore = 70; // Base score
  if (cookies.length > 0) {
    const sensitivePatterns = ['session', 'auth', 'token', 'jwt', 'csrf', 'sid', 'login', 'password'];
    let secureCookies = 0;
    let totalSensitiveCookies = 0;
    
    cookies.forEach((cookie: any) => {
      const isSensitive = sensitivePatterns.some(pattern => 
        cookie.name?.toLowerCase().includes(pattern)
      );
      if (isSensitive) {
        totalSensitiveCookies++;
        const cookieSecure = cookie.httpOnly && cookie.secure && 
                            (cookie.sameSite !== 'None' || cookie.secure);
        if (cookieSecure) secureCookies++;
      }
    });
    
    if (totalSensitiveCookies > 0) {
      const secureRatio = secureCookies / totalSensitiveCookies;
      cookieScore = 50 + Math.round(secureRatio * 50);
    } else {
      cookieScore = 100; // No sensitive cookies = full score
    }
  } else {
    cookieScore = 100; // No cookies = full score
  }
  cookieScore = Math.max(50, Math.min(100, cookieScore));
  
  // Component 5: HTTPS & SSL/TLS (10% weight)
  let httpsScore = 50; // Base score
  const isHttps = scan.url?.startsWith('https://');
  if (!isHttps) {
    httpsScore = 20;
  } else {
    httpsScore = 70;
    const httpsScripts = scripts.filter((s: any) => s.src?.startsWith('https://')).length;
    const httpsRatio = totalScripts > 0 ? httpsScripts / totalScripts : 1;
    httpsScore += Math.round(httpsRatio * 20);
    
    if (sslCertificate?.valid) {
      httpsScore += 10;
      if (sslCertificate.daysUntilExpiry && sslCertificate.daysUntilExpiry > 30) {
        httpsScore += 5;
      }
    } else if (sslCertificate && !sslCertificate.valid) {
      httpsScore -= 15;
    }
  }
  httpsScore = Math.max(20, Math.min(100, httpsScore));
  
  // Component 6: Third-party Security (10% weight)
  let thirdPartyScore = 80; // Base score
  if (thirdPartyDomains.length > 0) {
    if (thirdPartyDomains.length <= 5) {
      thirdPartyScore = 100;
    } else if (thirdPartyDomains.length <= 10) {
      thirdPartyScore = 90;
    } else if (thirdPartyDomains.length <= 15) {
      thirdPartyScore = 80;
    } else if (thirdPartyDomains.length <= 20) {
      thirdPartyScore = 70;
    } else if (thirdPartyDomains.length <= 30) {
      thirdPartyScore = 60;
    } else {
      thirdPartyScore = 50;
    }
    
    const thirdPartyScripts = scripts.filter((s: any) => s.isThirdParty);
    const dangerousThirdParty = thirdPartyScripts.filter((s: any) => 
      s.containsEval || s.containsFunction
    ).length;
    if (thirdPartyScripts.length > 0) {
      const dangerousRatio = dangerousThirdParty / thirdPartyScripts.length;
      thirdPartyScore -= Math.round(dangerousRatio * 15);
    }
  }
  thirdPartyScore = Math.max(50, Math.min(100, thirdPartyScore));
  
  // Component 7: Vulnerability Assessment (10% weight)
  let vulnerabilityScore = 80; // Base score
  if (vulnerabilities.length > 0) {
    const highSeverity = vulnerabilities.filter((v: any) => 
      typeof v === 'string' ? v.toLowerCase().includes('critical') || v.toLowerCase().includes('high') :
      v.severity === 'high' || v.severity === 'critical'
    ).length;
    const mediumSeverity = vulnerabilities.filter((v: any) => 
      typeof v === 'string' ? v.toLowerCase().includes('medium') :
      v.severity === 'medium'
    ).length;
    const lowSeverity = vulnerabilities.filter((v: any) => 
      typeof v === 'string' ? v.toLowerCase().includes('low') :
      v.severity === 'low'
    ).length;
    
    vulnerabilityScore -= (highSeverity * 10);
    vulnerabilityScore -= (mediumSeverity * 5);
    vulnerabilityScore -= (lowSeverity * 2);
  }
  vulnerabilityScore = Math.max(40, Math.min(100, vulnerabilityScore));
  
  // Calculate weighted average (always recalculate to match backend)
  const rawScore = Math.round(
    (cspScore * 0.25) +
    (scriptSecurityScore * 0.20) +
    (headersScore * 0.15) +
    (cookieScore * 0.10) +
    (httpsScore * 0.10) +
    (thirdPartyScore * 0.10) +
    (vulnerabilityScore * 0.10)
  );
  
  // Apply improved minimum floor
  const hasDecentSecurity = 
    cspScore >= 40 || 
    scriptSecurityScore >= 50 || 
    headersScore >= 50 || 
    httpsScore >= 60;
  
  const complianceScore = hasDecentSecurity 
    ? Math.max(45, Math.min(100, rawScore)) // Minimum 45 if decent security exists
    : Math.max(35, Math.min(100, rawScore)); // Minimum 35 otherwise
  
  // Check scan method (headless vs HTTP fallback)
  const scanMethod = results?.scanMethod || 'headless';
  const scanMethodReason = results?.scanMethodReason || '';
  const usedHttpFallback = scanMethod === 'http-fallback';
  
  // Debug logging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('🍪 Cookie extraction debug:', {
      hasResults: !!results,
      hasCookies: !!results?.cookies,
      cookiesType: typeof results?.cookies,
      cookiesIsArray: Array.isArray(results?.cookies),
      cookiesCount: cookies.length,
      scanMethod: scanMethod,
      usedHttpFallback: usedHttpFallback,
      sampleCookie: cookies.length > 0 ? cookies[0] : null
    });
  }
  
  const localStorage = results?.localStorage || {};
  const sessionStorage = results?.sessionStorage || {};
  const requestParameters = results?.requestParameters || [];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-5 w-5 text-purple-500" />;
      case "running":
        return <Clock className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-purple-600 bg-green-50 border-green-200";
      case "running":
        return "text-blue-600 bg-blue-50 border-blue-200";
      default:
        return "text-red-600 bg-red-50 border-red-200";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link href="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Scan Report
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Detailed security analysis for {scan.url}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              const link = document.createElement('a');
              link.href = `/api/export/scan-report/${scan.id}`;
              link.download = `scan-report-${scan.id}-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button variant="outline" size="sm">
            <ExternalLink className="h-4 w-4 mr-2" />
            Visit Site
          </Button>
        </div>
      </div>

      {/* Scan Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-purple-600" />
            Scan Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">URL</label>
              <div className="mt-1 font-mono text-sm bg-slate-50 dark:bg-slate-800 p-2 rounded">
                {scan.url}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Type</label>
              <div className="mt-1">
                <Badge variant="outline">
                  {scan.type === 'full' ? 'Full Security Scan' : scan.type === 'crawl' ? 'Single crawl' : scan.type}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Status</label>
              <div className="mt-1 flex items-center gap-2">
                {getStatusIcon(scan.status)}
                <span className={`px-2 py-1 rounded text-sm font-medium border ${getStatusColor(scan.status)}`}>
                  {scan.status}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Scanned</label>
              <div className="mt-1 text-sm">
                {formatDistanceToNow(new Date(scan.createdAt), { addSuffix: true })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Crawl: discovered URLs in domain (single crawl only) */}
      {scan.type === "crawl" && Array.isArray(results?.discoveredUrls) && results.discoveredUrls.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Discovered URLs in domain ({results.discoveredUrls.length})
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Same-origin pages found from the seed URL. This single crawl includes the seed page and this list; no separate scan was run for each URL.
            </p>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm max-h-64 overflow-y-auto rounded border p-3">
              {results.discoveredUrls.map((u: string, i: number) => (
                <li key={i} className="truncate font-mono" title={u}>
                  <a href={u} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{u}</a>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Security Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCode2 className="h-5 w-5 text-blue-600" />
              JavaScript Files
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{scripts.length}</div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {highRiskScripts.length} high-risk scripts detected
            </p>
            {highRiskScripts.length > 0 && (
              <div className="mt-2">
                <Badge variant="destructive" className="text-xs">
                  ⚠️ Requires attention
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-purple-600" />
              External Domains
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{thirdPartyDomains.length}</div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Third-party dependencies detected
            </p>
            {thirdPartyDomains.length > 5 && (
              <div className="mt-2">
                <Badge variant="secondary" className="text-xs">
                  📊 Review recommended
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-600" />
              Security Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {complianceScore}%
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Overall security rating
            </p>
            <div className="mt-2">
              <Badge variant={complianceScore >= 70 ? "default" : complianceScore >= 40 ? "secondary" : "destructive"} className="text-xs">
                {complianceScore >= 70 ? "✅ Compliant" : complianceScore >= 40 ? "⚠️ Needs Improvement" : "❌ Non-Compliant"}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content and Sidebar Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Side (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* JavaScript Analysis */}
          {scripts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCode2 className="h-5 w-5 text-purple-600" />
                  JavaScript Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {scripts.slice(0, 10).map((script: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">
                            {script.src === 'inline' ? `Inline Script #${index + 1}` : script.src}
                          </span>
                          {script.isThirdParty && (
                            <Badge variant="outline" className="text-xs">Third-party</Badge>
                          )}
                          {script.containsEval && (
                            <Badge variant="destructive" className="text-xs">eval()</Badge>
                          )}
                          {script.containsFunction && (
                            <Badge variant="secondary" className="text-xs">Function()</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-600 dark:text-slate-400">
                          <span>Size: {Math.round(script.length / 1024)}KB</span>
                          <span>Entropy: {script.entropy.toFixed(2)}</span>
                          <span className="flex items-center gap-1">
                            <Hash className="h-3 w-3" />
                            {script.contentHash.substring(0, 12)}...
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${
                          script.containsEval || script.containsFunction ? 'text-red-600' : 
                          script.entropy > 6 ? 'text-yellow-600' : 'text-purple-600'
                        }`}>
                          {script.containsEval || script.containsFunction ? 'High Risk' : 
                           script.entropy > 6 ? 'Medium Risk' : 'Low Risk'}
                        </span>
                      </div>
                    </div>
                  ))}
                  {scripts.length > 10 && (
                    <div className="text-center pt-4">
                      <Link href="/scripts">
                        <Button variant="outline">
                          View All {scripts.length} Scripts
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Third-Party Domains */}
          {thirdPartyDomains.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-purple-600" />
                  External Dependencies
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {thirdPartyDomains.map((domain: any, index: number) => (
                    <div key={index} className="p-3 border rounded-lg bg-slate-50 dark:bg-slate-800/50">
                      <div className="font-medium text-sm mb-1">
                        {typeof domain === 'string' ? domain : domain.domain || domain.name}
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-400">
                        External dependency
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Sidebar - Request Parameters (1/3 width) */}
        <div className="lg:col-span-1 space-y-6">
          {/* Request Parameters Sidebar */}
          {requestParameters.length > 0 && (
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-purple-600" />
                  Request Parameters
                </CardTitle>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  {requestParameters.length} captured
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {requestParameters.slice(0, 50).map((param: any, index: number) => {
                    // Check if this is a form input
                    const isFormInput = param.resourceType === 'form' || param.source === 'form';
                    const displayUrl = param.url || param.endpoint || param.formAction || '';
                    const displayName = param.name || 'Unnamed';
                    const displayValue = param.value || '';
                    
                    return (
                      <div key={index} className="p-2 border rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">{param.method || 'GET'}</Badge>
                          <Badge variant="secondary" className="text-xs">{param.resourceType || 'document'}</Badge>
                          {isFormInput && (
                            <Badge variant="default" className="text-xs bg-purple-100 text-purple-800">Form Input</Badge>
                          )}
                        </div>
                        
                        {/* For form inputs, show name and value */}
                        {isFormInput ? (
                          <>
                            <div className="text-xs font-medium mb-1">
                              <span className="text-slate-600 dark:text-slate-400">Name:</span> {displayName}
                            </div>
                            {displayValue && (
                              <div className="text-xs font-mono truncate mb-1 text-slate-600 dark:text-slate-400" title={displayValue}>
                                Value: {String(displayValue).substring(0, 30)}{String(displayValue).length > 30 ? '...' : ''}
                              </div>
                            )}
                            {param.type && (
                              <div className="text-xs text-slate-500 mb-1">
                                Type: {param.type}
                              </div>
                            )}
                            {displayUrl && (
                              <div className="text-xs font-mono truncate mt-1 text-slate-500" title={displayUrl}>
                                Form: {displayUrl}
                              </div>
                            )}
                            {param.placeholder && (
                              <div className="text-xs text-slate-500 mt-1">
                                Placeholder: {param.placeholder}
                              </div>
                            )}
                            {param.required && (
                              <Badge variant="outline" className="text-xs mt-1">Required</Badge>
                            )}
                          </>
                        ) : (
                          <>
                            {/* For HTTP requests, show URL and query params */}
                            {displayUrl && (
                              <div className="text-xs font-mono truncate mb-1" title={displayUrl}>
                                {displayUrl}
                              </div>
                            )}
                            {param.queryParams && Object.keys(param.queryParams).length > 0 && (
                              <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                                <span className="font-medium">Params:</span>
                                <div className="mt-1 space-y-0.5">
                                  {Object.entries(param.queryParams).slice(0, 2).map(([key, value]: [string, any]) => (
                                    <div key={key} className="font-mono text-xs">
                                      {key}={String(value).substring(0, 15)}{String(value).length > 15 ? '...' : ''}
                                    </div>
                                  ))}
                                  {Object.keys(param.queryParams).length > 2 && (
                                    <div className="text-xs text-slate-500">
                                      +{Object.keys(param.queryParams).length - 2} more
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Show name/value if present (for other parameter types) */}
                            {param.name && !param.queryParams && (
                              <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                                <span className="font-medium">Name:</span> {param.name}
                                {param.value && (
                                  <span className="ml-2 font-mono">
                                    = {String(param.value).substring(0, 20)}{String(param.value).length > 20 ? '...' : ''}
                                  </span>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                  {requestParameters.length > 50 && (
                    <div className="text-center pt-2 border-t">
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        Showing 50 of {requestParameters.length}
                      </p>
                      <Link href="/request-parameters">
                        <Button variant="outline" size="sm" className="mt-2 w-full">
                          View All Parameters
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Full-width sections below */}
      <div className="space-y-6">
      {/* Cookies Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cookie className="h-5 w-5 text-purple-600" />
            Cookies Analysis {cookies.length > 0 && `(${cookies.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {usedHttpFallback && cookies.length === 0 && (
            <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                    Cookies Not Captured - HTTP Fallback Used
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-2">
                    This scan used HTTP fallback instead of the headless browser service. HTTP fallback cannot capture cookies, localStorage, or sessionStorage.
                  </p>
                  {scanMethodReason && (
                    <p className="text-xs text-yellow-600 dark:text-yellow-400 italic mb-2">
                      Reason: {scanMethodReason}
                    </p>
                  )}
                  <div className="text-xs text-yellow-700 dark:text-yellow-300">
                    <p className="font-medium mb-1">To capture cookies:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2">
                      <li>Ensure the headless browser service is running on port 8001</li>
                      <li>Check PM2 logs: <code className="bg-yellow-100 dark:bg-yellow-900 px-1 rounded">pm2 logs pci-scan-report | grep headless</code></li>
                      <li>Verify service health: <code className="bg-yellow-100 dark:bg-yellow-900 px-1 rounded">curl http://localhost:8001/health</code></li>
                      <li>Restart the application if needed: <code className="bg-yellow-100 dark:bg-yellow-900 px-1 rounded">pm2 restart pci-scan-report</code></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          {cookies.length > 0 ? (
            <div className="space-y-3">
              {cookies.map((cookie: any, index: number) => {
                // Handle cookie name - might be in different formats
                const cookieName = cookie.name || cookie.key || `Cookie ${index + 1}`;
                const cookieDomain = cookie.domain || cookie.domainName || 'N/A';
                const cookiePath = cookie.path || '/';
                const httpOnly = cookie.httpOnly === true || cookie.httpOnly === 'true';
                const secure = cookie.secure === true || cookie.secure === 'true';
                const sameSite = cookie.sameSite || cookie.sameSiteAttribute || 'Unset';
                const hasIssues = (!httpOnly || !secure || sameSite === 'Unset' || sameSite === 'None');
                
                return (
                  <div key={index} className={`p-3 border rounded-lg ${hasIssues ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'bg-slate-50 dark:bg-slate-800/50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm font-mono">{cookieName}</span>
                      {hasIssues && <AlertTriangle className="h-4 w-4 text-yellow-600" />}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">Domain:</span>
                        <span className="ml-1 font-mono text-xs">{cookieDomain}</span>
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">Path:</span>
                        <span className="ml-1 font-mono text-xs">{cookiePath}</span>
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">HttpOnly:</span>
                        {httpOnly ? (
                          <CheckCircle className="h-3 w-3 inline ml-1 text-green-500" />
                        ) : (
                          <XCircle className="h-3 w-3 inline ml-1 text-red-500" />
                        )}
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">Secure:</span>
                        {secure ? (
                          <CheckCircle className="h-3 w-3 inline ml-1 text-green-500" />
                        ) : (
                          <XCircle className="h-3 w-3 inline ml-1 text-red-500" />
                        )}
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">SameSite:</span>
                        <Badge 
                          variant="outline" 
                          className={`ml-1 text-xs ${
                            sameSite === 'Strict' || sameSite === 'Lax' 
                              ? 'border-green-500 text-green-700' 
                              : sameSite === 'None' 
                              ? 'border-orange-500 text-orange-700'
                              : 'border-red-500 text-red-700'
                          }`}
                        >
                          {sameSite}
                        </Badge>
                      </div>
                    </div>
                    {hasIssues && (
                      <div className="mt-2 text-xs text-yellow-700 dark:text-yellow-300">
                        <AlertTriangle className="h-3 w-3 inline mr-1" />
                        Security issues detected: {[
                          !httpOnly && 'Missing HttpOnly',
                          !secure && 'Missing Secure flag',
                          (sameSite === 'Unset' || sameSite === 'None') && 'Insecure SameSite'
                        ].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <Cookie className="h-12 w-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <p className="text-sm font-medium mb-2">No Cookies Detected</p>
              <p className="text-xs">
                This scan did not capture any cookies. This could mean:
              </p>
              <ul className="text-xs mt-2 space-y-1 text-left max-w-md mx-auto">
                <li>• The website doesn't set cookies</li>
                <li>• Cookies were blocked by browser settings</li>
                <li>• The scan used HTTP fallback (which doesn't capture cookies)</li>
                <li>• The website requires JavaScript to set cookies</li>
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Storage Analysis */}
      {(Object.keys(localStorage).length > 0 || Object.keys(sessionStorage).length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-purple-600" />
              Browser Storage Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.keys(localStorage).length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    LocalStorage ({Object.keys(localStorage).length} items)
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(localStorage).map(([key, value]: [string, any]) => (
                      <div key={key} className="p-2 border rounded-lg bg-slate-50 dark:bg-slate-800/50">
                        <div className="font-medium text-sm mb-1">{key}</div>
                        <div className="text-xs text-slate-600 dark:text-slate-400 font-mono truncate">
                          {String(value).substring(0, 50)}{String(value).length > 50 ? '...' : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Object.keys(sessionStorage).length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    SessionStorage ({Object.keys(sessionStorage).length} items)
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(sessionStorage).map(([key, value]: [string, any]) => (
                      <div key={key} className="p-2 border rounded-lg bg-slate-50 dark:bg-slate-800/50">
                        <div className="font-medium text-sm mb-1">{key}</div>
                        <div className="text-xs text-slate-600 dark:text-slate-400 font-mono truncate">
                          {String(value).substring(0, 50)}{String(value).length > 50 ? '...' : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
}